import axios from 'axios'

// Projects API client (uses cookie auth)
const projectsClient = axios.create({
  baseURL: '/api/user',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

export interface ProjectSource {
  id: number
  project_id: number
  source_type: 'audio' | 'video' | 'url'
  label: string | null
  original_filename: string | null
  duration_seconds: number | null
  offset_ms: number
  is_primary: boolean
  status: 'pending' | 'processing' | 'ready' | 'failed'
  created_at: string
}

export interface ProjectDocument {
  id: number
  project_id: number
  doc_type: string | null
  original_filename: string | null
  status: 'pending' | 'processing' | 'ready' | 'failed'
  created_at: string
}

export interface Project {
  id: number
  title: string | null
  source_url: string | null
  status: 'draft' | 'pending' | 'processing' | 'waiting_input' | 'completed' | 'failed'
  current_stage_index: number
  audio_duration_seconds: number | null
  source_count: number
  created_at: string
  updated_at: string
}

export interface CreateProjectRequest {
  title?: string
  pipeline_id?: number
}

// Create a new empty project
export const createProject = async (data: CreateProjectRequest = {}): Promise<Project> => {
  const response = await projectsClient.post<Project>('/projects', data)
  return response.data
}

// Get user's projects
export const getProjects = async (limit = 20, offset = 0): Promise<Project[]> => {
  const response = await projectsClient.get<Project[]>('/projects', {
    params: { limit, offset },
  })
  return response.data
}

// Upload a source file
export const uploadSource = async (
  projectId: number,
  file: File,
  label?: string,
  sourceType = 'audio'
): Promise<ProjectSource> => {
  const formData = new FormData()
  formData.append('file', file)

  const params = new URLSearchParams()
  if (label) params.append('label', label)
  params.append('source_type', sourceType)

  const response = await projectsClient.post<ProjectSource>(
    `/projects/${projectId}/sources?${params.toString()}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )
  return response.data
}

// Upload a document
export const uploadDocument = async (
  projectId: number,
  file: File,
  docType = 'other'
): Promise<ProjectDocument> => {
  const formData = new FormData()
  formData.append('file', file)

  const params = new URLSearchParams()
  params.append('doc_type', docType)

  const response = await projectsClient.post<ProjectDocument>(
    `/projects/${projectId}/documents?${params.toString()}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )
  return response.data
}

// Get project sources
export const getSources = async (projectId: number, token: string): Promise<ProjectSource[]> => {
  const response = await projectsClient.get<ProjectSource[]>(
    `/projects/${projectId}/sources`,
    {
      headers: { 'X-Access-Token': token },
    }
  )
  return response.data
}

// Add a cloud storage URL as source
export const addSourceUrl = async (
  projectId: number,
  url: string,
  label?: string
): Promise<ProjectSource> => {
  const params = new URLSearchParams()
  params.append('url', url)
  if (label) params.append('label', label)

  const response = await projectsClient.post<ProjectSource>(
    `/projects/${projectId}/sources/url?${params.toString()}`
  )
  return response.data
}

// Start project processing
export const startProject = async (projectId: number): Promise<{ success: boolean; message: string }> => {
  const response = await projectsClient.post<{ success: boolean; message: string }>(
    `/projects/${projectId}/start`
  )
  return response.data
}

// Update source
export const updateSource = async (
  projectId: number,
  sourceId: number,
  data: { label?: string; is_primary?: boolean; offset_ms?: number }
): Promise<ProjectSource> => {
  const response = await projectsClient.patch<ProjectSource>(
    `/projects/${projectId}/sources/${sourceId}`,
    data
  )
  return response.data
}

// Delete project
export const deleteProject = async (projectId: number): Promise<{ success: boolean; message: string }> => {
  const response = await projectsClient.delete<{ success: boolean; message: string }>(
    `/projects/${projectId}`
  )
  return response.data
}

export default projectsClient
